import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Fill missing details": free lookup of missing companies (CRM domain match) + phones (company website).
export default function ListEnrichButton({ listId, autoFill = false, onDone, onAutoChange }) {
  const [job, setJob] = useState(null);
  const [auto, setAuto] = useState(!!autoFill);
  const toggleAuto = async () => {
    const next = !auto;
    setAuto(next);
    try {
      await axios.post(`${API}/lists/${listId}/auto-fill`, { enabled: next });
      onAutoChange && onAutoChange();
      toast.success(next ? 'Weekly auto-fill on (Sundays 2am)' : 'Weekly auto-fill off');
    } catch { setAuto(!next); toast.error('Could not update auto-fill'); }
  };
  const timer = useRef(null);
  const running = job && ['queued', 'running'].includes(job.status);

  const poll = useCallback(async (notify) => {
    try {
      const { data } = await axios.get(`${API}/lists/${listId}/enrich`);
      const j = data.job;
      setJob(j);
      if (j && ['queued', 'running'].includes(j.status)) {
        timer.current = setTimeout(() => poll(true), 2000);
      } else if (notify && j) {
        if (j.status === 'done') toast.success(`Done: ${j.phones_found} phones, ${j.companies_linked} companies found`);
        else toast.error('Lookup failed - please try again');
        onDone && onDone();
      }
    } catch { /* ignore */ }
  }, [listId, onDone]);

  useEffect(() => { poll(false); return () => clearTimeout(timer.current); }, [poll]);

  const start = async () => {
    try {
      const { data } = await axios.post(`${API}/lists/${listId}/enrich`);
      setJob(data);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => poll(true), 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start lookup'); }
  };

  const todo = job ? Math.max(job.to_crawl || 0, 1) : 1;
  const pct = job ? Math.min(100, Math.round(((job.processed || 0) / todo) * 100)) : 0;

  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="list-enrich">
      <button type="button" onClick={start} disabled={running} data-testid="list-enrich-btn"
        title="Free lookup: links contacts to CRM companies by email domain and finds phone numbers on company websites. Never overwrites existing data."
        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-[#00A79D] text-[#00A79D] hover:bg-[#00A79D]/10 disabled:opacity-60 transition-colors">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        {running ? 'Looking up…' : 'Fill missing details'}
      </button>
      <label className="inline-flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer select-none" title="Automatically fill missing details on this list every Sunday at 2am">
        <input type="checkbox" checked={auto} onChange={toggleAuto} className="accent-[#00A79D] w-3.5 h-3.5" data-testid="list-autofill-toggle" />
        Auto-fill weekly
      </label>
      {running && (
        <div className="w-56" data-testid="list-enrich-progress">
          <div className="h-1.5 bg-slate-700/40 rounded-full overflow-hidden">
            <div className="h-full bg-[#00A79D] transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-slate-400 mt-1 text-right">
            {job.status === 'queued' ? 'Starting…' : `Checked ${job.processed || 0}/${job.to_crawl || 0} websites · ${job.phones_found || 0} phones · ${job.companies_linked || 0} companies`}
          </p>
        </div>
      )}
      {job && job.status === 'done' && (
        <p className="text-[11px] text-slate-400" data-testid="list-enrich-summary">
          {job.trigger === 'weekly' ? 'Last auto-run' : 'Last run'}{job.finished_at ? ` (${new Date(job.finished_at).toLocaleDateString('en-GB')})` : ''}: {job.phones_found} phones, {job.companies_linked} companies found{job.no_website ? ` · ${job.no_website} without a website` : ''}{job.remaining ? ` · more left, run again to continue` : ''}
        </p>
      )}
    </div>
  );
}
